import { useCallback, useEffect, useRef, useState } from "react";

type TrackPreviewOptions = {
  /** Max preview duration in ms. Default: 30000 */
  maxDurationMs?: number;
  /** Called when a preview ends (30s cap or track ended naturally) */
  onTrackEnd?: (trackId: string) => void;
  /** Called when preview starts — use to pause the global player */
  pauseGlobalPlayer?: () => void;
};

type TrackPreviewReturn = {
  isPlaying: boolean;
  /** True from the moment play() is called until audio actually starts (or fails) */
  isLoading: boolean;
  /** 0-1 progress over maxDurationMs */
  progress: number;
  currentTrackId: string | null;
  currentTimeMs: number;
  play: (src: string, trackId: string) => void;
  pause: () => void;
  togglePlay: (src: string, trackId: string) => void;
  stop: () => void;
};

export function useTrackPreview(
  options?: TrackPreviewOptions,
): TrackPreviewReturn {
  const maxMs = options?.maxDurationMs ?? 30_000;

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTrackId, setCurrentTrackId] = useState<string | null>(null);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  const currentTrackIdRef = useRef<string | null>(null);
  const rafIdRef = useRef(0);
  const startTimeRef = useRef(0);
  const accumulatedRef = useRef(0);
  /** Incremented on every play() call so stale async callbacks bail out */
  const playSeqRef = useRef(0);

  // Stable ref to options callbacks to avoid stale closures
  const onTrackEndRef = useRef(options?.onTrackEnd);
  onTrackEndRef.current = options?.onTrackEnd;
  const pauseGlobalRef = useRef(options?.pauseGlobalPlayer);
  pauseGlobalRef.current = options?.pauseGlobalPlayer;

  const handleEnded = useCallback(() => {
    cancelRaf();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    isPlayingRef.current = false;
    setIsPlaying(false);
    const trackId = currentTrackIdRef.current;
    if (trackId) {
      onTrackEndRef.current?.(trackId);
    }
  }, []);

  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const el = new Audio();
      el.addEventListener("ended", handleEnded);
      audioRef.current = el;
    }
    return audioRef.current;
  }, [handleEnded]);

  const cancelRaf = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
  }, []);

  const stopInternal = useCallback(() => {
    cancelRaf();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    isPlayingRef.current = false;
    setIsPlaying(false);
  }, [cancelRaf]);

  const startTicker = useCallback(() => {
    const tick = () => {
      if (!isPlayingRef.current) return;

      const elapsed =
        performance.now() - startTimeRef.current + accumulatedRef.current;
      const clamped = Math.min(elapsed, maxMs);
      const p = clamped / maxMs;

      setCurrentTimeMs(clamped);
      setProgress(p);

      if (clamped >= maxMs) {
        handleEnded();
        return;
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
  }, [maxMs, handleEnded]);

  const play = useCallback(
    (src: string, trackId: string) => {
      const audio = getAudio();
      const seq = ++playSeqRef.current;

      // If same track and just paused, resume
      if (currentTrackIdRef.current === trackId && audio.src && !audio.ended) {
        pauseGlobalRef.current?.();
        setIsLoading(true);
        audio
          .play()
          .then(() => {
            if (playSeqRef.current !== seq) return;
            setIsLoading(false);
            startTimeRef.current = performance.now();
            isPlayingRef.current = true;
            setIsPlaying(true);
            startTicker();
          })
          .catch(() => {
            if (playSeqRef.current !== seq) return;
            setIsLoading(false);
            isPlayingRef.current = false;
            setIsPlaying(false);
          });
        return;
      }

      // New track
      cancelRaf();
      pauseGlobalRef.current?.();

      currentTrackIdRef.current = trackId;
      setCurrentTrackId(trackId);
      setIsLoading(true);
      accumulatedRef.current = 0;
      setProgress(0);
      setCurrentTimeMs(0);

      audio.src = src;
      audio.load();
      audio
        .play()
        .then(() => {
          if (playSeqRef.current !== seq) return;
          setIsLoading(false);
          startTimeRef.current = performance.now();
          isPlayingRef.current = true;
          setIsPlaying(true);
          startTicker();
        })
        .catch(() => {
          if (playSeqRef.current !== seq) return;
          setIsLoading(false);
          isPlayingRef.current = false;
          setIsPlaying(false);
        });
    },
    [getAudio, cancelRaf, startTicker],
  );

  const pause = useCallback(() => {
    cancelRaf();
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
    accumulatedRef.current += performance.now() - startTimeRef.current;
    isPlayingRef.current = false;
    setIsPlaying(false);
  }, [cancelRaf]);

  const stop = useCallback(() => {
    stopInternal();
    const audio = audioRef.current;
    if (audio) {
      audio.removeAttribute("src");
      audio.load();
    }
    accumulatedRef.current = 0;
    currentTrackIdRef.current = null;
    setCurrentTrackId(null);
    setProgress(0);
    setCurrentTimeMs(0);
  }, [stopInternal]);

  const togglePlay = useCallback(
    (src: string, trackId: string) => {
      if (currentTrackIdRef.current === trackId && isPlayingRef.current) {
        pause();
      } else {
        play(src, trackId);
      }
    },
    [pause, play],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelRaf();
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeEventListener("ended", handleEnded);
        audio.src = "";
        audioRef.current = null;
      }
    };
  }, [cancelRaf, handleEnded]);

  return {
    isPlaying,
    isLoading,
    progress,
    currentTrackId,
    currentTimeMs,
    play,
    pause,
    togglePlay,
    stop,
  };
}
